import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Plus, Search } from 'lucide-react';
import EmptyState from '../governed-mcp-gateway/components/EmptyState';
import { fieldClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import OmniChatAgentDialog from './OmniChatAgentDialog';
import { omniChatIcon, OMNICHAT_PATHS } from './omniChatConstants';
import { logOmniChat } from './omniChatLogger';
import { useOmniChatPage } from './OmniChatScreen';
import { useOmniChat } from './omniChatStore';
import type { OmniChatAgent, OmniChatFormState, OmniChatGateway } from './omniChatTypes';

function AgentCard({
  agent,
  gateway,
  isLight,
  onOpen,
}: {
  agent: OmniChatAgent;
  gateway?: OmniChatGateway;
  isLight: boolean;
  onOpen: () => void;
}) {
  const Icon = omniChatIcon(agent.iconId);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
          }`}
        >
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
            {agent.title}
          </h3>
          <p className={`text-[11px] mt-0.5 truncate ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
            {gateway?.name ?? 'No gateway'}
          </p>
        </div>
      </div>

      <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {agent.description || 'No description yet.'}
      </p>

      <div className={`mt-4 pt-4 border-t flex flex-wrap items-center gap-1.5 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
            agent.active
              ? isLight
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-emerald-500/15 text-emerald-300'
              : isLight
                ? 'bg-neutral-100 text-neutral-500'
                : 'bg-neutral-800 text-neutral-400'
          }`}
        >
          {agent.active ? 'Active' : 'Inactive'}
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
            isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
          }`}
        >
          {gateway?.model ?? 'Unassigned'}
        </span>
      </div>
    </article>
  );
}

export default function OmniChatListPage() {
  const { isLight, onToast } = useOmniChatPage();
  const { agents, ready, saveAgent, findGateway } = useOmniChat();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready) logOmniChat('app', 'list_open', { count: agents.length });
  }, [agents.length, ready]);

  const filtered = useMemo(() => {
    const hay = query.trim().toLowerCase();
    if (!hay) return agents;
    return agents.filter((agent) => {
      const gateway = findGateway(agent.gatewayId);
      return `${agent.title} ${agent.description} ${gateway?.name ?? ''} ${gateway?.model ?? ''}`
        .toLowerCase()
        .includes(hay);
    });
  }, [agents, query]);

  const openCreate = () => setDialogOpen(true);

  const persist = async (form: OmniChatFormState) => {
    if (!form.title.trim() || !form.instruction.trim()) {
      onToast('error', 'Add a title and instruction before saving.');
      return;
    }
    if (form.gatewayId && !form.inferenceQuotaId) {
      onToast('error', 'Select an LLM gateway key before saving.');
      return;
    }
    setSaving(true);
    try {
      await saveAgent(form);
      onToast('success', 'Agent created.');
      setDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save this agent.';
      logOmniChat('error', 'dialog_save_failed', { message });
      onToast('error', message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className={`flex items-center justify-center py-24 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <Loader size={16} className="animate-spin mr-2" />
        Loading agents…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
            Platform
          </p>
          <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>OmniChat</h1>
          <p className={`mt-1.5 max-w-2xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>
            Create agents with an instruction and a gateway.
          </p>
        </div>
        <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
          <Plus size={14} />
          Add Agent
        </button>
      </div>

      <div className="relative max-w-xl">
        <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
        <input
          className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
          placeholder="Search agents or gateways…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title={query.trim() ? 'No matching agents' : 'No agents yet'}
          description={
            query.trim()
              ? 'Try a different name, description, or gateway.'
              : 'Add an agent to describe how it should talk and which gateway it uses.'
          }
          action={
            query.trim() ? null : (
              <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
                <Plus size={14} />
                Add Agent
              </button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              gateway={findGateway(agent.gatewayId)}
              isLight={isLight}
              onOpen={() => navigate(OMNICHAT_PATHS.detail(agent.id))}
            />
          ))}
        </div>
      )}

      <OmniChatAgentDialog
        isLight={isLight}
        open={dialogOpen}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={persist}
      />
    </div>
  );
}
